import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Text,
  Button,
  Card,
  CardFooter,
  Checkbox,
  Spinner,
  Input,
  Tooltip,
  Link,
  Label,
  Dropdown,
  Option,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
} from '@fluentui/react-components';
import {
  Add20Regular,
  ArrowSwap24Regular,
  VehicleTruck24Regular,
  ChevronRight16Regular,
  Edit16Regular,
  LockClosed16Regular,
  Warning16Regular,
  ArrowRight20Regular,
  Box24Regular,
  Delete16Regular,
  Info16Regular,
  MoreHorizontal20Regular,
  Receipt20Regular,
  Settings20Regular,
  Dismiss24Regular,
  Dismiss16Regular,
  Checkmark16Regular,
} from '@fluentui/react-icons';
import Header from '../components/Header';
import DatePicker from '../components/DatePicker';
import OrderStatusPill from '../components/OrderStatusPill';
import PaymentStatusPill from '../components/PaymentStatusPill';
import PhaseOrderDrawer from '../components/PhaseOrderDrawer';
import PhaseLineAdd from '../components/PhaseLineAdd';
import { ProductSearchInput, ProductGroup, StockItem, fullBinLabel } from '../components/ProductSearchInput';
import PhaseSchedulePanel from '../components/PhaseSchedulePanel';
import ProhibitedItemsAck from '../components/ProhibitedItemsAck';
import ProjectStatusBadge from '../components/ProjectStatusBadge';
import AddressFields from '../components/AddressFields';
import {
  CustomerSection,
  LocationSection,
  BillingAddressSection,
  DetailsSection,
} from '../components/orderForm';
import { getGeocodeConnector } from '../../../shared/maps';
import { apiFetch } from '../lib/apiFetch';
import { queryKeys } from '../lib/queryClient';
import {
  fetchProject,
  addProjectPhase,
  savePhaseSchedule,
  updateProject,
  clearPhaseLines,
  addPhaseHeavyLine,
  removePhaseHeavyLine,
  reservePhase,
  deletePhase,
  fetchPhaseBinLine,
  updatePhaseLinePrice,
  Project,
  ProjectPhase,
  FINAL_PICKUP_LABEL,
  isFinalPickup,
} from '../services/projectService';
import {
  applyPhaseEdit,
  addDays,
  scheduleChanged,
  mergeScheduleWithServer,
  EditablePhase,
  CascadeMode,
} from '../lib/phaseDates';
import { fetchCustomerLocations, addCustomerLocation } from '../services/customerLocationService';
import { useIsMobile } from '../hooks/useIsMobile';

// A phase with money against its invoice (paid / partially paid / overpaid) has
// a frozen rental window — its dates can't be rescheduled from the project (the
// invoice is finalized/immutable). Staff extend it via an Extra Day charge on
// the order instead. Mirrors the backend guard in PUT /projects/<id>/phases/dates.
const PAID_PAYMENT_STATES = ['paid', 'partially_paid', 'overpaid'];
const isPhasePaid = (p?: ProjectPhase | null): boolean =>
  !!p && PAID_PAYMENT_STATES.includes(p.paymentStatus || '');

// YYYY-MM-DD <-> Date helpers, local time so the calendar doesn't drift a day
// for users east of UTC (mirrors the order form's BillingRentalSection).
const isoToDate = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const dateToYmd = (date?: Date | null): string => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
// Phase-card dates render in a tight two-up row (Delivery → Pickup), so we drop
// the year — phases share a near-term window and "Jun 15 → Jun 22" is the rental
// mental model. The year still shows in the schedule panel / timeline.
const formatDateShort = (date: Date): string =>
  date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
// Phase windows are stored as midnight-UTC ISO; the schedule editor works in
// YYYY-MM-DD. Slicing the UTC date portion avoids any local-timezone drift.
const isoToYmd = (iso?: string | null): string | null => (iso ? iso.slice(0, 10) : null);
const ymdToIso = (ymd: string | null): string | null => (ymd ? `${ymd}T00:00:00.000Z` : null);

// Standard rental length — a newly added swap defaults to this many days so it
// drops in fully dated (delivery hinged to the prior pickup, pickup +7). Matches
// the backend default in add_project_phase.
const STANDARD_RENTAL_DAYS = 7;

// Project phases → the editable schedule shape the date pickers drive. Used to
// seed the local copy and to recompute the server baseline for change detection.
const seedSchedule = (phases: ProjectPhase[]): EditablePhase[] =>
  phases.map((p) => ({
    id: p.id,
    phaseNumber: p.phaseNumber,
    startDate: isoToYmd(p.startDate),
    endDate: isoToYmd(p.endDate),
    promoted: !!p.booqable_id,
  }));

const useStyles = makeStyles({
  workspaceCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    ...shorthands.borderRadius('8px'),
    ...shorthands.margin('8px'),
    // Mobile: full-bleed work surface — drop the margin gutter and rounded
    // corners so the content uses every pixel of width.
    '@media (max-width: 768px)': {
      ...shorthands.margin('0'),
      ...shorthands.borderRadius('0'),
    },
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 64px)',
    // The card is a flex child of the column-flex .main-content scroller; its
    // explicit min-height equals the viewport, which would otherwise let flexbox
    // shrink the white card below its (taller) content — phase cards then spill
    // out the bottom onto the gray background. Pin shrink so the card always
    // grows to contain its content and .main-content scrolls instead.
    flexShrink: 0,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    flexWrap: 'wrap',
  },
  // Inline project-name editor that lives in the header title slot. The name
  // reads as plain title text but is a button: hover reveals a subtle surface +
  // pencil so it's discoverable as editable; clicking swaps in the Input.
  nameWrap: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '6px',
    flexWrap: 'wrap',
    minWidth: 0,
  },
  nameButton: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
    fontFamily: 'inherit',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    ...shorthands.border('1px', 'solid', 'transparent'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('2px', '6px'),
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorStrokeFocus2,
    },
  },
  // Empty-name placeholder ("Untitled project") reads muted/italic to signal
  // it's a prompt rather than a real name.
  nameEmpty: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  nameEditIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  nameInput: {
    minWidth: '220px',
  },
  // The immutable P-#### identifier trailing the editable name.
  idSuffix: {
    color: tokens.colorNeutralForeground3,
  },
  // Grey scrolling backdrop beneath the inline Header — mirrors OrderDetails'
  // mainColumn so the white section cards inside read as inset blocks rather
  // than white-on-white. Bottom corners are rounded to tuck inside the card's
  // 8px radius (we can't use overflow:hidden — it would clip the phase bin
  // picker's absolutely-positioned dropdown).
  body: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  // Centered work area — readable max-width so content doesn't stretch
  // edge-to-edge on wide monitors (mirrors OrderDetails' workArea).
  workArea: {
    width: '100%',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
    ...shorthands.padding('16px'),
    // Extra bottom breathing room so a freshly-added phase card clears the
    // fold without forcing a scroll to see it in full.
    paddingBottom: '120px',
  },
  // Top row of section cards — two columns (Customer + Location on the left,
  // Billing address + Details on the right), mirroring OrderDetails' top row.
  // Collapses to a single column on narrow screens.
  topRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    ...shorthands.gap('16px'),
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr',
    },
  },
  topColumn: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    minWidth: 0,
  },
  infoLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  // Read-only date display in the provisioning stage (mirrors phaseDates layout).
  phaseDatesReadonly: {
    display: 'flex',
    // Align to the bottom (the date value) so the arrow lines up with the dates,
    // not the vertical middle of the label+value stack.
    alignItems: 'flex-end',
    columnGap: '8px',
  },
  dateValue: {
    fontSize: '14px',
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  // Steer note shown on a paid phase (frozen window) in place of an editable hint.
  paidDatesNote: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '6px',
    marginTop: '8px',
    color: tokens.colorNeutralForeground3,
  },
  // Readonly arrow: sized to the date-value line and bottom-aligned with it, so
  // it centers on the dates rather than the taller label+value column.
  arrowWrapReadonly: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: tokens.lineHeightBase300,
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  // Muted placeholder where the bin picker will appear once provisioning starts.
  binPlaceholder: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
  // Heavy-material toggle under a 10cy/20cy bin row. Indented to sit under the
  // bin slot's content, smaller-weight so it reads as a secondary affordance.
  heavyCheckbox: {
    paddingLeft: '4px',
    '& .fui-Checkbox__label': {
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorNeutralForeground2,
    },
  },
  // "Not reserved" cue under a bin row: the bin landed but the phase is still a
  // draft (auto-reserve failed). Pumpkin accent (warning, not error) per the
  // design system — neutral text, pumpkin icon — plus an inline retry.
  notReservedRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '6px',
    paddingLeft: '4px',
  },
  notReservedIcon: {
    color: tokens.colorPalettePumpkinBorderActive,
    flexShrink: 0,
  },
  notReservedText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  // Default-bin picker dialog body.
  lockDialogBody: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
  },
  // Footer actions size to their full label (no truncation/wrap) so both the
  // "Skip…" and "Lock…" labels stay on one line.
  lockDialogButton: {
    whiteSpace: 'nowrap',
    minWidth: 'fit-content',
  },
  settingsHeader: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  // A grouped section within the settings drawer (heading + controls).
  settingsSection: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
    ...shorthands.padding('16px', '0'),
  },
  // Trailing "add a swap" tile — a dashed slot that sits as the last cell of the
  // phase grid. The whole tile is the click target (a button), not just an inner
  // control, so the full area is the hit zone.
  addSlot: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: '6px',
    width: '100%',
    minHeight: '160px',
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border('1px', 'dashed', tokens.colorNeutralStroke2),
    // White slot so the dashed tile reads as an empty card on the grey
    // work-area backdrop (alongside the white phase cards).
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    fontFamily: 'inherit',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      ...shorthands.borderColor(tokens.colorBrandStroke1),
    },
    ':disabled': {
      cursor: 'default',
      color: tokens.colorNeutralForegroundDisabled,
      ...shorthands.borderColor(tokens.colorNeutralStroke2),
    },
  },
  // Empty-state ("Add first phase") variant: stand the tile up to a full phase
  // card's height so it doesn't read as a stubby button before any phase exists.
  // (When phases exist the grid already stretches the tile to the row height.)
  addSlotEmpty: {
    minHeight: '200px',
  },
  // Same dashed-card shell, but a static container (not itself a button) — used
  // once phases exist, where the slot holds two explicit action buttons (Add
  // swap / Add final pickup) rather than being one big click target.
  addSlotStatic: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: '10px',
    width: '100%',
    minHeight: '160px',
    ...shorthands.padding('16px'),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border('1px', 'dashed', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // The two add-phase actions, stacked so they stay readable in a narrow grid
  // cell. Each button stretches to the slot width so the pair reads as a tidy
  // segmented group.
  addSlotButtons: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    width: '100%',
    maxWidth: '220px',
  },
  addActionButton: {
    width: '100%',
    justifyContent: 'flex-start',
  },
  addSlotInner: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  // Helper line baked into the tile (replaces the hover tooltip): the
  // invoiceable-phase hint normally, the disabled reason when blocked.
  addSlotCaption: {
    maxWidth: '240px',
    textAlign: 'center',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  // Responsive grid of phase cards: 1 column on mobile, stepping up to 4 across
  // on wide screens. Mobile-first base (1fr) + MUTUALLY-EXCLUSIVE min/max bands
  // so exactly one rule matches at any width — overlapping max-width queries let
  // griffel's media ordering pick the wrong one (a ~400px phone matched all of
  // ≤640/≤960/≤1280 and rendered 2 cols instead of 1). minmax(0, 1fr) lets cells
  // shrink below their content's intrinsic width instead of overflowing.
  timeline: {
    display: 'grid',
    ...shorthands.gap('12px'),
    gridTemplateColumns: '1fr',
    '@media (min-width: 641px) and (max-width: 960px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@media (min-width: 961px) and (max-width: 1280px)': {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
    '@media (min-width: 1281px)': {
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    },
  },
  // Card chrome: zero Card's own padding/gap so the header/body/footer regions
  // can carry full-bleed backgrounds. We deliberately do NOT set overflow:hidden
  // — that would clip the product picker's absolutely-positioned dropdown to the
  // card bounds. The header/footer round their outer corners instead.
  phaseCard: {
    ...shorthands.padding(0),
    ...shorthands.gap(0),
    // Suppress Card's own focus-within ring: clicking a control inside (e.g. the
    // ⋯ menu) briefly moves focus into the card, flashing a thick brand-colored
    // border before focus hops to the popover. The inner controls carry their own
    // focus indicators, so the card-level one is just noise.
    '&[data-fui-focus-within]:focus-within::after': {
      ...shorthands.borderColor('transparent'),
    },
  },
  cardHeader: {
    // Two stacked bands: line 1 = identity (chip · name · status pill) and is
    // the only clickable region (opens the order drawer); line 2 = display id +
    // the action bar on a slightly darker brand band, kept un-clickable so its
    // buttons don't fight a parent handler. Fluent's CardHeader couldn't do
    // this (fixed image/header/action grid), so the header is hand-rolled.
    display: 'flex',
    flexDirection: 'column',
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground,
  },
  // Line 1 (identity) is the sole click target — opens the order drawer. The
  // growing title shoves the status pill to the right edge.
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    ...shorthands.padding('12px', '16px'),
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorBrandBackgroundHover },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorNeutralStrokeOnBrand,
      outlineOffset: '-2px',
    },
  },
  // Line 2 sits on a slightly darker brand band and is NOT clickable, so the
  // display id grows to push the action bar (invoice · open · ⋯) to the right
  // without a parent handler intercepting the button clicks.
  headerBottom: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    ...shorthands.padding('10px', '16px'),
    backgroundColor: tokens.colorBrandBackgroundPressed,
  },
  // Title + id on the brand-blue header need inverted (light) text. The title
  // grows to fill line 1 and truncates so a long phase label never crowds the
  // status pill.
  headerTitle: {
    color: tokens.colorNeutralForegroundOnBrand,
    flexGrow: 1,
    minWidth: 0,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // The display id grows on line 2 to push the action bar to the right.
  headerDesc: {
    color: tokens.colorNeutralForegroundInverted2,
    flexGrow: 1,
    minWidth: 0,
  },
  // Order # link on the brand band. White (on-brand) so it reads as actionable
  // against the brand-blue header; opens the full order in a new tab. Sized to
  // its text (no flexGrow) so only the "#<number>" is the click target — a
  // sibling spacer pushes the action bar right.
  headerOrderLink: {
    color: tokens.colorNeutralForegroundOnBrand,
    flexShrink: 0,
    ':hover': { color: tokens.colorNeutralForegroundOnBrand },
    ':active': { color: tokens.colorNeutralForegroundOnBrand },
  },
  // Body sits on a lighter surface than the header/footer caps.
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
    flexGrow: 1,
    ...shorthands.padding('12px', '16px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: '12px',
    rowGap: '8px',
    flexWrap: 'wrap',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // Keep delivery + pickup on a single row even in a narrow grid cell: the two
  // columns flex-shrink (flexBasis 0 / minWidth 0) instead of wrapping.
  phaseDates: {
    display: 'flex',
    alignItems: 'flex-end',
    columnGap: '8px',
    flexWrap: 'nowrap',
  },
  dateColumn: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '4px',
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  arrowWrap: {
    display: 'flex',
    alignItems: 'center',
    // Match the medium DatePicker height so the arrow centers on the fields.
    height: '32px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '20px',
    flexWrap: 'wrap',
  },
  totalsBar: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '24px',
    ...shorthands.padding('20px', '24px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('6px'),
    flexWrap: 'wrap',
  },
  // Prohibited-items acknowledgment block — one per project, gates reserving
  // every phase. White card with a heading + status line above the control.
  ackBar: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
    ...shorthands.padding('16px', '24px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('6px'),
  },
  ackHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    flexWrap: 'wrap',
  },
  ackStatusPending: {
    fontSize: '13px',
    color: tokens.colorPalettePumpkinBorderActive,
  },
  // Pending acknowledgment marker: a required-field red asterix + an info icon
  // whose tooltip carries the "can't be reserved until confirmed" reason, so the
  // header stays quiet instead of spelling the whole consequence out.
  requiredAsterisk: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: 700,
  },
  infoIcon: {
    color: tokens.colorNeutralForeground3,
    cursor: 'help',
    display: 'inline-flex',
  },
  ackStatusDone: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    ...shorthands.padding('48px'),
  },
  // Page-header action group: "Edit details" alongside the primary Add button.
  headerActionGroup: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
  },
  // Line-2 action bar: invoice · open-order · ⋯. Sits flush right (the display
  // id grows to push it over); no divider needed now it owns its own row.
  // minHeight matches the small action button (24px) so the brand band keeps a
  // constant height even on un-promoted drafts where the bar (and the order link)
  // render nothing — otherwise line 2 collapses to just its padding and that
  // card's header reads shorter than its promoted siblings.
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '4px',
    flexShrink: 0,
    minHeight: '24px',
  },
  // ⋯ button sits on the brand-blue header — force white so it's visible.
  headerKebab: {
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': { color: tokens.colorNeutralForegroundOnBrand },
    ':hover:active': { color: tokens.colorNeutralForegroundOnBrand },
  },
  deleteMenuItem: {
    color: tokens.colorPaletteRedForeground1,
  },
  deleteConfirmButton: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteRedBackground3),
    ':hover': {
      backgroundColor: tokens.colorPaletteRedForeground1,
      color: tokens.colorNeutralForegroundOnBrand,
      ...shorthands.borderColor(tokens.colorPaletteRedForeground1),
    },
    ':hover:active': {
      backgroundColor: tokens.colorPaletteRedForeground1,
      color: tokens.colorNeutralForegroundOnBrand,
      ...shorthands.borderColor(tokens.colorPaletteRedForeground1),
    },
  },
});

const money = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

// Roll-off sizes (cubic yards) eligible for the Heavy material add-on, per the
// rolloff_sizes catalog (migration 045): only 10cy and 20cy carry heavy material
// — 15/30/40 do not. Kept in sync with that per-size business rule.
const HEAVY_ELIGIBLE_SIZES = [10, 20];

// Whether a bin's mirrored product title denotes a heavy-eligible roll-off size.
// Parses the first size token off the label, e.g. "Roll-off Dumpster - 10cy" → 10.
const binIsHeavyEligible = (binLabel?: string | null): boolean => {
  if (!binLabel) return false;
  const m = binLabel.match(/\d+/);
  return !!m && HEAVY_ELIGIBLE_SIZES.includes(parseInt(m[0], 10));
};

const useBinRowStyles = makeStyles({
  // Bordered slot sized to match the product dropdown's box exactly (same
  // min-height, border, radius) so the bin and its inline Remove button occupy
  // the same bounding box the picker did — no layout shift on add.
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: '8px',
    width: '100%',
    minHeight: '40px',
    boxSizing: 'border-box',
    ...shorthands.padding('2px', '6px', '2px', '12px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    minWidth: 0,
  },
  title: {
    fontSize: '14px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  muted: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  // Trailing action cluster (price-edit + remove, or the price editor itself).
  trailing: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('2px'),
    flexShrink: 0,
  },
  // The override editor's $-prefixed input, kept narrow so the row doesn't grow.
  priceInput: {
    width: '92px',
  },
  // Override error, shown on a second line below the row.
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    marginTop: '4px',
  },
});

// The bin row inside an editable phase card: shows the actual bin line item with
// the Remove button inline. The label comes straight off the phase payload
// (mirrored server-side on the write path — no live call on render). Falls back
// to an item count when the label hasn't been mirrored yet.
//
// When `phaseId` is set and the row isn't read-only, it also exposes an inline
// per-unit price override: the bin's live line (id + current price) is fetched
// on edit-open (the mirror carries only the title), edited in a $-input, and
// committed through the shared order-line price endpoint. `onPriceOverridden`
// lets the parent refetch so the card's order total reflects the new price.
const PhaseBinRow: React.FC<{
  binLabel?: string | null;
  binQuantity?: number | null;
  itemCount: number;
  removing?: boolean;
  onRemove?: () => void;
  // Read-only: drop the remove + price-edit affordances. Used in the planning
  // stage, where a promoted phase's bin is shown for context but isn't editable
  // until the user returns to provisioning.
  readOnly?: boolean;
  // The underlying order id. Enables the price-override editor when set.
  phaseId?: string;
  // The bin line's id + per-unit price (cents), mirrored on the phase payload so
  // the editor seeds instantly. When absent (legacy mirror), the editor falls
  // back to a live /bin-line fetch on open.
  binLineId?: string | null;
  binUnitPriceInCents?: number | null;
  // Fired after a successful price override so the parent can refetch totals.
  onPriceOverridden?: () => void;
}> = ({
  binLabel,
  binQuantity,
  itemCount,
  removing = false,
  onRemove,
  readOnly = false,
  phaseId,
  binLineId,
  binUnitPriceInCents,
  onPriceOverridden,
}) => {
  const styles = useBinRowStyles();
  // Price-override editor state. `editing` swaps the trailing cluster for the
  // $-input + save/cancel; `loading` covers the on-open live line fetch; `lineId`
  // is the Booqable line the commit targets; `value` is the in-progress dollars.
  const [editing, setEditing] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [lineId, setLineId] = React.useState<string | null>(null);
  const [value, setValue] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const canEditPrice = !readOnly && !!phaseId && !!binLabel;

  const openEditor = async () => {
    setEditing(true);
    setError(null);
    // Fast path: the bin line id + price ride along on the phase payload, so the
    // input opens populated and editable with zero lag.
    if (binLineId) {
      setLineId(binLineId);
      setValue(((binUnitPriceInCents ?? 0) / 100).toFixed(2));
      return;
    }
    // Legacy fallback: a bin whose mirror predates id/price capture — fetch the
    // live line once. The next override backfills the mirror, so this is one-time.
    setLoading(true);
    try {
      const line = await fetchPhaseBinLine(phaseId as string);
      setLineId(line.lineId);
      setValue((line.priceEachInCents / 100).toFixed(2));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the price.');
    } finally {
      setLoading(false);
    }
  };

  const cancelEditor = () => {
    setEditing(false);
    setLineId(null);
    setValue('');
    setError(null);
  };

  const saveEditor = async () => {
    // Distinguish "the bin line never loaded" from "the typed price is bad" —
    // conflating them blamed the price when the real failure was the on-open
    // fetch (no lineId).
    if (!lineId) {
      setError('Couldn’t load the bin line — close and reopen to retry.');
      return;
    }
    const dollars = parseFloat(value);
    if (isNaN(dollars) || dollars < 0) {
      setError('Enter a valid price.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updatePhaseLinePrice(phaseId as string, lineId, Math.round(dollars * 100));
      onPriceOverridden?.();
      cancelEditor();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update the price.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className={styles.row}>
        <div className={styles.info}>
          {binLabel ? (
            <Text className={styles.title}>
              {binQuantity && binQuantity > 1 ? `${binQuantity}× ${binLabel}` : binLabel}
            </Text>
          ) : (
            <Text className={styles.muted}>
              {itemCount} {itemCount === 1 ? 'item' : 'items'}
            </Text>
          )}
        </div>

        {editing ? (
          <div className={styles.trailing}>
            {loading ? (
              <Spinner size="tiny" label="Loading price…" labelPosition="before" />
            ) : (
              <>
                <Input
                  className={styles.priceInput}
                  size="small"
                  type="number"
                  min={0}
                  step="0.01"
                  contentBefore={<Text>$</Text>}
                  value={value}
                  disabled={saving}
                  onChange={(_e, data) => setValue(data.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEditor();
                    if (e.key === 'Escape') cancelEditor();
                  }}
                  aria-label="Override unit price (dollars)"
                />
                <Button
                  appearance="subtle"
                  size="small"
                  icon={saving ? <Spinner size="tiny" /> : <Checkmark16Regular />}
                  disabled={saving}
                  onClick={saveEditor}
                  aria-label="Save price"
                />
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Dismiss16Regular />}
                  disabled={saving}
                  onClick={cancelEditor}
                  aria-label="Cancel price edit"
                />
              </>
            )}
          </div>
        ) : (
          <div className={styles.trailing}>
            {canEditPrice && (
              <Tooltip content="Override price" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Edit16Regular />}
                  onClick={openEditor}
                  aria-label="Override price"
                />
              </Tooltip>
            )}
            {!readOnly && onRemove && (
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete16Regular />}
                disabled={removing}
                onClick={onRemove}
                aria-label={removing ? 'Removing bin' : 'Remove bin'}
              />
            )}
          </div>
        )}
      </div>
      {error && <Text className={styles.error}>{error}</Text>}
    </div>
  );
};

const ProjectDetails: React.FC = () => {
  const styles = useStyles();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Boundary dragging on the timeline strip is a fine-pointer affordance — it's
  // fiddly on touch. On mobile the strip drops to a read-only overview and dates
  // are edited via the phase cards' native date pickers below instead.
  const isMobile = useIsMobile();

  // The phase whose order summary is open in the side drawer (null = closed).
  // Read-only — all edits happen in the full order, reached via the drawer's
  // "Open full order" action. Keeping the project page mounted preserves context.
  const [drawerPhase, setDrawerPhase] = React.useState<ProjectPhase | null>(null);

  // Live "draft" service location driving the Location block. Seeded from the
  // project and updated as the user explores the map picker, so the block's
  // current-location card and the picker's commit button reflect the explored
  // pin before anything persists. Committing runs saveLocation; canceling
  // reverts it to the project's stored location via the picker's revert path.
  const [draftLocation, setDraftLocation] = React.useState<
    { lat: number; lng: number; address: string; name: string } | null
  >(null);

  // The phase pending a delete-confirm (null = dialog closed).
  const [deleteTarget, setDeleteTarget] = React.useState<ProjectPhase | null>(null);

  // Project settings drawer (right side). Houses behaviour preferences like the
  // cascade mode; future project settings will live here too.
  const [settingsOpen, setSettingsOpen] = React.useState(false);

  // Billing-address dialog (project-owned override). Mirrors the order form's
  // billing dialog: a Saved-address picker (customer vs. new) on top of the
  // shared AddressFields. Fields compose into the display string we persist.
  const [billingDialogOpen, setBillingDialogOpen] = React.useState(false);
  const [billingMode, setBillingMode] = React.useState<'customer' | 'new'>('new');
  const [billingLine1, setBillingLine1] = React.useState('');
  const [billingLine2, setBillingLine2] = React.useState('');
  const [billingCity, setBillingCity] = React.useState('');
  const [billingRegion, setBillingRegion] = React.useState('');
  const [billingPostal, setBillingPostal] = React.useState('');
  const [billingCountry, setBillingCountry] = React.useState('United States');

  // Project-details editor (gov/procurement fields). The project name is no
  // longer here — it's edited in situ in the header title (see the inline name
  // editor below). Seeded from the project; committed via updateProject, which
  // mirrors the gov fields onto the project's draft phases so they reach each
  // phase's invoice.
  const [detailsForm, setDetailsForm] = React.useState({
    poNumber: '',
    contractNumber: '',
    solicitationNumber: '',
  });

  // Inline header name editor. `editingName` swaps the title text for an Input;
  // `nameDraft` holds the in-progress value. Commit on Enter/blur, cancel on
  // Escape. Kept separate from detailsForm so a name change saves on its own.
  const [editingName, setEditingName] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState('');

  const { data: project, isLoading, isError } = useQuery<Project>({
    queryKey: queryKeys.projects.detail(projectId || ''),
    queryFn: () => fetchProject(projectId as string),
    enabled: !!projectId,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.projects.detail(projectId || '') });

  // Saved locations for this project's customer — offered in the site picker
  // alongside dropping a fresh pin (mirrors the order form's location section).
  const customerLocationsQuery = useQuery({
    queryKey: queryKeys.customers.locations(project?.customerId || ''),
    queryFn: () => fetchCustomerLocations(project!.customerId as string),
    enabled: !!project?.customerId,
  });
  const savedLocations = React.useMemo(
    () =>
      (customerLocationsQuery.data || [])
        .filter((l) => l.latitude != null && l.longitude != null)
        .map((l) => ({
          id: l.id,
          name: l.name || l.address || undefined,
          address: l.address || '',
          lat: Number(l.latitude),
          lng: Number(l.longitude),
        })),
    [customerLocationsQuery.data],
  );

  // The project's customer record — for the read-only Customer card (email) and
  // the derived Billing address block. The project payload carries only the
  // customer's name + id; GET /customers/<id> (dual-id) adds email + the primary
  // address we surface as the billing address.
  const customerQuery = useQuery<{
    name?: string;
    email?: string;
    address?: {
      address1?: string | null;
      address2?: string | null;
      city?: string | null;
      region?: string | null;
      zipcode?: string | null;
      country?: string | null;
    } | null;
  }>({
    queryKey: ['db-customer', project?.customerId],
    queryFn: async () => {
      const res = await apiFetch(`/customers/${project!.customerId}`);
      if (!res.ok) throw new Error(`Failed to fetch customer: ${res.statusText}`);
      return (await res.json()).data;
    },
    enabled: !!project?.customerId,
  });

  // The customer's primary address as both a one-line display and the discrete
  // fields the billing dialog seeds from. Null when the customer has no address
  // on file. Used to pre-fill "Add billing address" so staff can snapshot the
  // customer's address in one click, mirroring the order form's billing dialog.
  const customerMainAddress = React.useMemo(() => {
    const a = customerQuery.data?.address;
    if (!a) return null;
    const fields = {
      address1: (a.address1 || '').trim(),
      address2: (a.address2 || '').trim(),
      city: (a.city || '').trim(),
      region: (a.region || '').trim(),
      zipcode: (a.zipcode || '').trim(),
      country: (a.country || '').trim() || 'United States',
    };
    const display = [a.address1, a.address2, a.city, a.region, a.zipcode, a.country]
      .map((s) => (s || '').trim())
      .filter(Boolean)
      .join(', ');
    return display ? { display, fields } : null;
  }, [customerQuery.data]);

  // Customer shape the read-only CustomerSection card expects.
  const customerForCard = React.useMemo(
    () =>
      project
        ? {
            id: project.customerId || '',
            name: customerQuery.data?.name || project.customerName || '',
            email: customerQuery.data?.email || '',
          }
        : null,
    [project, customerQuery.data],
  );

  // Saved customer locations in the {label, lat, lng} shape LocationSection's
  // "Choose from saved locations" menu reads. Same order as `savedLocations`,
  // so an index from the menu maps straight back for the apply handler.
  const locationSectionLocations = React.useMemo(
    () => savedLocations.map((l) => ({ label: l.name || l.address, lat: l.lat, lng: l.lng })),
    [savedLocations],
  );

  // Local, instant-edit copy of the phase schedule. Date pickers read from here
  // so a pick reflects immediately (no per-field API round-trip = no lag); the
  // whole chain commits on "Save schedule". Seeded from the query and after each
  // save; preserved across background refetches while the user has unsaved edits
  // (unless the phase count changes, e.g. a swap was added — then reseed).
  const [schedule, setSchedule] = React.useState<EditablePhase[]>([]);
  // "Touched" flag: the user has interacted with the schedule this session. Used
  // only to preserve in-progress edits across background refetches; whether the
  // edits actually differ from the server is `hasScheduleChanges` below.
  const [scheduleDirty, setScheduleDirty] = React.useState(false);

  // The server's current schedule, re-derived on every fetch. The Save/Discard
  // bar and the add-phase guard key off a real value diff against this (a pick
  // that lands back on the original value is not a "change").
  const serverSchedule = React.useMemo(
    () => (project ? seedSchedule(project.phases) : []),
    [project],
  );
  const hasScheduleChanges = React.useMemo(
    () => scheduleChanged(serverSchedule, schedule),
    [serverSchedule, schedule],
  );

  // Timeline strip data: the live (possibly edited) schedule supplies dates +
  // promoted state; the phase records supply label + status for the fill color.
  // Joined by order id so the strip reflects unsaved date edits immediately.
  const timelineSegments = React.useMemo(() => {
    if (!project) return [];
    const metaById = new Map(project.phases.map((p) => [p.id, p]));
    return schedule.map((s) => {
      const meta = metaById.get(s.id);
      return {
        id: s.id,
        phaseNumber: s.phaseNumber,
        label: meta?.phaseLabel || `Phase ${s.phaseNumber}`,
        startDate: s.startDate,
        endDate: s.endDate,
        status: meta?.booqableStatus,
        promoted: s.promoted,
        // Paid phases have a frozen window — the strip locks every boundary that
        // touches one (see timelineBoundaries).
        paid: isPhasePaid(meta),
        // The final pickup is pickup-only — the strip draws it as a terminal
        // marker, not a proportional segment.
        pickupOnly: !!meta && isFinalPickup(meta),
      };
    });
  }, [project, schedule]);

  React.useEffect(() => {
    if (!project) return;
    // An optimistic add owns the schedule manually until its real order id lands
    // (onSuccess swaps it in). Don't let a background refetch adopt/merge over it
    // mid-flight — that would drop the placeholder or double-insert it.
    if (schedule.some((p) => p.id.startsWith('temp-'))) return;
    const server = seedSchedule(project.phases);
    // No unsaved edits: adopt the server schedule as-is.
    if (!scheduleDirty) {
      setSchedule(server);
      setScheduleDirty(false);
      return;
    }
    // Unsaved edits present. A background refetch with the same phase set leaves
    // the local copy untouched. When the set changes — adding a swap no longer
    // forces a save first — merge so the new phase appears WITHOUT discarding the
    // pending edits (the new phase chains onto the previous phase's edited end).
    if (server.length === schedule.length) return;
    setSchedule((local) => mergeScheduleWithServer(local, server));
  }, [project, scheduleDirty, schedule.length]);

  // Snapshot of the schedule a save is persisting, captured at mutate time, so
  // onSuccess can tell whether the user edited again while the request was in
  // flight (a drag released and re-grabbed before the save round-trip finished).
  const savedSnapshotRef = React.useRef<EditablePhase[] | null>(null);
  const saveSchedule = useMutation({
    mutationFn: () => {
      // Skip any optimistic placeholder whose real order id hasn't landed yet
      // — its temp id isn't a saveable order_id.
      const snapshot = schedule;
      savedSnapshotRef.current = snapshot;
      return savePhaseSchedule(
        projectId as string,
        snapshot
          .filter((p) => !p.id.startsWith('temp-'))
          .map((p) => ({
            order_id: p.id,
            starts_at: ymdToIso(p.startDate),
            stops_at: ymdToIso(p.endDate),
          })),
      );
    },
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.projects.detail(projectId || ''), updated);
      // Only mark clean (and let the project-sync effect adopt the server
      // schedule) when nothing changed during the save. If the user dragged a
      // boundary again while this request was in flight, the local schedule now
      // holds newer edits — keep them and stay dirty so the server response
      // doesn't clobber them and the next auto-save pass persists them.
      const saved = savedSnapshotRef.current;
      if (!saved || !scheduleChanged(saved, scheduleRef.current)) {
        setScheduleDirty(false);
      }
    },
  });

  const onPhaseDate = (phaseId: string, edge: 'start' | 'end', date: Date | null) => {
    // Cascade is always ripple now (the toggle was removed): a boundary edit
    // shifts every later phase by the same delta, preserving their durations.
    const mode: CascadeMode = 'ripple';
    const ymd = dateToYmd(date) || null;
    // A final pickup is pickup-only — its single card field drives its START (the
    // collection hinge), and its hidden end always trails by a day so the 1-day
    // event stays a valid, lockable window. Ripple can't fill that end when there
    // was no prior date to ripple from (a final pickup added before the schedule
    // was dated), so re-assert the invariant here.
    const isFp = (project?.phases || []).some(
      (ph) => ph.id === phaseId && isFinalPickup(ph),
    );
    setSchedule((prev) => {
      const next = applyPhaseEdit(prev, { phaseId, edge, date: ymd, mode });
      if (!isFp || edge !== 'start') return next;
      return next.map((p) =>
        p.id === phaseId && p.startDate
          ? { ...p, endDate: addDays(p.startDate, 1) }
          : p,
      );
    });
    setScheduleDirty(true);
  };

  // Same ripple edit, but driven by the timeline strip's draggable boundaries —
  // the date arrives already as a clamped YYYY-MM-DD string. Auto-save picks it
  // up via the same debounce as the date pickers.
  const onBoundaryEdit = React.useCallback(
    (phaseId: string, edge: 'start' | 'end', date: string) => {
      setSchedule((prev) => applyPhaseEdit(prev, { phaseId, edge, date, mode: 'ripple' }));
      setScheduleDirty(true);
    },
    [],
  );

  const discardSchedule = () => {
    if (!project) return;
    setSchedule(seedSchedule(project.phases));
    setScheduleDirty(false);
  };

  // --- Auto-save the schedule (no Save button) ---------------------------------
  // Date edits live in `schedule`; a debounced commit writes the whole chain so a
  // burst of picks coalesces into one save. Refs let the unmount flush read the
  // latest values without re-subscribing the cleanup on every edit.
  const scheduleRef = React.useRef(schedule);
  scheduleRef.current = schedule;
  const dirtyRef = React.useRef(hasScheduleChanges);
  dirtyRef.current = hasScheduleChanges;
  const savePendingRef = React.useRef(false);
  savePendingRef.current = saveSchedule.isPending;
  // True while an optimistic phase is awaiting its real order id — hold off
  // auto-saving until the id lands so we never POST a temp id.
  const hasTempPhase = schedule.some((p) => p.id.startsWith('temp-'));

  React.useEffect(() => {
    if (!hasScheduleChanges || saveSchedule.isPending || hasTempPhase) return;
    const t = setTimeout(() => {
      // Re-check at fire time: the user may have reverted, or a save may have
      // started in the debounce window.
      if (dirtyRef.current && !savePendingRef.current) saveSchedule.mutate();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, hasScheduleChanges, saveSchedule.isPending, hasTempPhase]);

  // Flush any unsaved edits if the page unmounts mid-debounce (e.g. navigate
  // away right after a pick), so a fast exit can't drop the last change.
  React.useEffect(
    () => () => {
      if (!dirtyRef.current || savePendingRef.current) return;
      savePhaseSchedule(
        projectId as string,
        scheduleRef.current
          .filter((p) => !p.id.startsWith('temp-'))
          .map((p) => ({
            order_id: p.id,
            starts_at: ymdToIso(p.startDate),
            stops_at: ymdToIso(p.endDate),
          })),
      ).catch(() => {
        /* best-effort; the next mount reseeds from the server */
      });
    },
    [projectId],
  );

  // Save-status pill state for the schedule toolbar.
  const saveStatus: 'saving' | 'unsaved' | 'error' | 'saved' = saveSchedule.isPending
    ? 'saving'
    : hasScheduleChanges
    ? 'unsaved'
    : saveSchedule.isError
    ? 'error'
    : 'saved';

  // --- Planning vs provisioning stage ----------------------------------------
  // Stage 1 (planning): a dates-only scratchpad — phases are un-promoted drafts,
  // the bin picker is hidden, and the contiguous date chain auto-saves. Stage 2
  // (provisioning): dates lock to read-only and each phase gets a bin (adding a
  // bin promotes the phase to Booqable). `stageOverride` lets the Lock / Edit
  // schedule buttons flip stages; until then we derive the stage from the data
  // (any phase already provisioned ⇒ start in provisioning).
  const [stageOverride, setStageOverride] = React.useState<'planning' | 'provisioning' | null>(
    null,
  );
  const derivedStage: 'planning' | 'provisioning' =
    project && project.phases.some((p) => p.itemCount > 0 || !!p.booqable_id)
      ? 'provisioning'
      : 'planning';
  const stage = stageOverride ?? derivedStage;

  // The bin every phase defaults to, chosen once when locking the schedule, so a
  // same-size swap job isn't re-picked per phase. Each empty phase offers a
  // one-click "Add <bin>"; the choice is per-session (not persisted).
  const [defaultBin, setDefaultBin] = React.useState<{
    group: ProductGroup;
    stockItem: StockItem;
    label: string;
  } | null>(null);
  const [lockDialogOpen, setLockDialogOpen] = React.useState(false);
  // Phases queued to auto-add the default bin once provisioning opens. Captured
  // at lock time (every then-empty phase); each card fires its add on mount and
  // clears itself from the set, so a later manual remove doesn't re-add.
  const [autoAddBinPhaseIds, setAutoAddBinPhaseIds] = React.useState<Set<string>>(
    () => new Set(),
  );

  // Every phase needs a window before bins can be chosen against it.
  const allPhasesDated = (schedule.length > 0) && schedule.every((p) => p.startDate && p.endDate);

  const confirmLock = (addBins: boolean) => {
    setLockDialogOpen(false);
    // "Lock & add bins": queue the chosen default bin onto every empty phase so
    // the cards add it automatically (no per-card "Add" click). "Skip" leaves the
    // cards on the manual picker.
    setAutoAddBinPhaseIds(
      addBins && defaultBin
        ? new Set(
            (project?.phases || [])
              // A final pickup carries no bin, so never auto-add one to it.
              .filter((p) => p.itemCount === 0 && !isFinalPickup(p))
              .map((p) => p.id),
          )
        : new Set(),
    );
    setStageOverride('provisioning');
  };

  // Monotonic counter for optimistic placeholder ids (avoids Date.now()).
  const tempPhaseSeq = React.useRef(0);
  const addPhase = useMutation({
    mutationFn: (vars: { finalPickup?: boolean } = {}) =>
      addProjectPhase(projectId as string, vars.finalPickup ? { final_pickup: true } : {}),
    // Optimistic insert: the create itself is a fast DB-only flush, but the
    // follow-up project refetch is slow — so render the card immediately rather
    // than waiting ~4s for the refetch. The real order id is swapped in the
    // instant the create resolves (onSuccess), well before the refetch lands.
    onMutate: async (vars) => {
      const finalPickup = !!vars?.finalPickup;
      const key = queryKeys.projects.detail(projectId || '');
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Project>(key);
      const tempId = `temp-${tempPhaseSeq.current++}`;
      const nextNum =
        (previous?.phases.reduce((m, p) => Math.max(m, p.phaseNumber || 0), 0) ?? 0) + 1;
      // Default a 7-day window (our standard rental): a swap hinges its delivery
      // onto the latest phase's (edited) pickup; the first phase (no predecessor)
      // anchors to today so it still lands dated with a timeline segment. A final
      // pickup is pickup-only — a single-day event on the last bin's pickup day.
      const latestEnd = schedule.length
        ? [...schedule].sort((a, b) => a.phaseNumber - b.phaseNumber)[schedule.length - 1].endDate
        : null;
      const startYmd = latestEnd ?? dateToYmd(new Date());
      const endYmd = addDays(startYmd, finalPickup ? 1 : STANDARD_RENTAL_DAYS);

      qc.setQueryData<Project>(key, (old) => {
        if (!old) return old;
        const placeholder: ProjectPhase = {
          id: tempId,
          booqable_id: null,
          phaseNumber: nextNum,
          // Mirrors the backend default (see add_project_phase): the first phase
          // is the "Initial drop"; later phases are swaps numbered from 1; the
          // project-closing pickup is the "Final pickup".
          phaseLabel: finalPickup
            ? FINAL_PICKUP_LABEL
            : nextNum === 1
              ? 'Initial drop'
              : `Swap ${nextNum - 1}`,
          phaseDisplayId: `${old.projectNumber}.${nextNum}`,
          startDate: ymdToIso(startYmd),
          endDate: ymdToIso(endYmd),
          booqableStatus: 'draft',
          paymentStatus: null,
          price: 0,
          paidAmount: 0,
          itemCount: 0,
          binLabel: null,
          binQuantity: null,
        };
        return { ...old, phases: [...old.phases, placeholder] };
      });
      // Mirror into the editable schedule and mark dirty so the defaulted window
      // is persisted by auto-save — this is what makes the 7-day default stick
      // even when the backend that served the create doesn't apply it yet (the
      // sandbox preview hits the master-tracking API).
      setSchedule((prev) => [
        ...prev,
        { id: tempId, phaseNumber: nextNum, startDate: startYmd, endDate: endYmd, promoted: false },
      ]);
      setScheduleDirty(true);
      return { previous, tempId };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(queryKeys.projects.detail(projectId || ''), ctx.previous);
      }
    },
    onSuccess: (data, _v, ctx) => {
      // Swap the temp id for the real order id so the card is editable (and
      // auto-saveable) immediately — the slow refetch in onSettled then fills
      // authoritative fields (display id etc.) in the background.
      const tempId = ctx?.tempId;
      if (!tempId) return;
      qc.setQueryData<Project>(queryKeys.projects.detail(projectId || ''), (old) =>
        old
          ? {
              ...old,
              phases: old.phases.map((p) =>
                p.id === tempId ? { ...p, id: data.order_id } : p,
              ),
            }
          : old,
      );
      setSchedule((prev) =>
        prev.map((p) => (p.id === tempId ? { ...p, id: data.order_id } : p)),
      );
    },
    onSettled: invalidate,
  });

  // Add a phase. From provisioning, drop back to planning so its (empty) dates
  // are editable — a phase added mid-provisioning is a fresh scratchpad row.
  const handleAddPhase = (finalPickup = false) => {
    if (addPhase.isPending) return;
    // Every phase mints an order against the project's SAVED service location and
    // requires the prohibited-items acknowledgment; without either the minted
    // order lands in a fail state. Gate creation on both — for every phase, not
    // just the first, since the location can be cleared mid-project. The disabled
    // tile mirrors this; guard here too so the gate holds if reached another way.
    const hasLocation =
      project?.deliveryLatitude != null && project?.deliveryLongitude != null;
    if (!hasLocation || !project?.prohibitedItemsAcknowledged) return;
    if (stage === 'provisioning') setStageOverride('planning');
    addPhase.mutate({ finalPickup });
  };

  // Open the settings drawer (behaviour preferences only now — project details
  // are edited inline in the Details block).
  const openSettings = () => setSettingsOpen(true);

  // Whether the form differs from what's persisted (drives Save/Discard). A pure
  // derived comparison — note it reads "dirty" before the first seed too (empty
  // form vs a named project), which is exactly why seeding is gated on the
  // separate `detailsTouched` flag below, NOT on this value.
  const detailsDirty =
    !!project &&
    (detailsForm.poNumber.trim() !== (project.poNumber || '') ||
      detailsForm.contractNumber.trim() !== (project.contractNumber || '') ||
      detailsForm.solicitationNumber.trim() !== (project.solicitationNumber || ''));

  // Seed the inline Details form from the project. Gated on `detailsTouched` (set
  // on the first edit, cleared on save/discard) so background refetches reseed
  // the untouched form but never clobber in-progress typing.
  const [detailsTouched, setDetailsTouched] = React.useState(false);

  const seedDetails = React.useCallback(() => {
    setDetailsForm({
      poNumber: project?.poNumber || '',
      contractNumber: project?.contractNumber || '',
      solicitationNumber: project?.solicitationNumber || '',
    });
  }, [project]);

  React.useEffect(() => {
    if (!project || detailsTouched) return;
    seedDetails();
  }, [project, detailsTouched, seedDetails]);

  const editDetail = (key: keyof typeof detailsForm, value: string) => {
    setDetailsTouched(true);
    setDetailsForm((f) => ({ ...f, [key]: value }));
  };

  const discardDetails = () => {
    seedDetails();
    setDetailsTouched(false);
  };

  const saveDetails = useMutation({
    mutationFn: () =>
      updateProject(projectId as string, {
        po_number: detailsForm.poNumber.trim() || null,
        contract_number: detailsForm.contractNumber.trim() || null,
        solicitation_number: detailsForm.solicitationNumber.trim() || null,
      }),
    onSuccess: () => {
      setDetailsTouched(false);
      invalidate();
    },
  });

  // --- Billing address (project-owned override) ------------------------------
  // The one-line display we persist into project.billingAddress, in the same
  // field order the customer address renders.
  const composeBillingDisplay = () =>
    [billingLine1, billingLine2, billingCity, billingRegion, billingPostal, billingCountry]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(', ');

  const billingComplete =
    !!billingCountry.trim() &&
    !!billingLine1.trim() &&
    !!billingCity.trim() &&
    !!billingRegion.trim() &&
    !!billingPostal.trim();

  // Open the dialog, pre-filling from the customer's saved address when there is
  // one (so "Add" defaults to snapshotting it). Falls back to a blank new form.
  const openBillingDialog = () => {
    const f = customerMainAddress?.fields;
    setBillingLine1(f?.address1 || '');
    setBillingLine2(f?.address2 || '');
    setBillingCity(f?.city || '');
    setBillingRegion(f?.region || '');
    setBillingPostal(f?.zipcode || '');
    setBillingCountry(f?.country || 'United States');
    setBillingMode(customerMainAddress ? 'customer' : 'new');
    setBillingDialogOpen(true);
  };

  const saveBilling = useMutation({
    mutationFn: (display: string) =>
      updateProject(projectId as string, { billing_address: display }),
    onSuccess: () => {
      setBillingDialogOpen(false);
      invalidate();
    },
  });

  // Remove writes the '' sentinel (explicitly removed), NOT null. null means
  // "inherit the customer's address" and would re-populate the card on reload;
  // '' keeps it empty until the user adds one. (See the tri-state handling in
  // update_project.)
  const removeBilling = useMutation({
    mutationFn: () => updateProject(projectId as string, { billing_address: '' }),
    // Clear the card immediately on X: write the '' (explicitly removed) sentinel
    // into the cached project so effectiveBillingAddress reads empty without
    // waiting for the round-trip. Roll back on error; reconcile on settle.
    onMutate: async () => {
      const key = queryKeys.projects.detail(projectId || '');
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Project>(key);
      qc.setQueryData<Project>(key, (old) => (old ? { ...old, billingAddress: '' } : old));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKeys.projects.detail(projectId || ''), context.previous);
      }
    },
    onSettled: invalidate,
  });

  // The address the card shows. project.billingAddress is tri-state:
  //   null  -> inherit: show the customer's saved address (auto-populated, live)
  //   ''    -> explicitly removed: show the empty "Add billing address" state
  //   text  -> an explicit project-level override
  const effectiveBillingAddress =
    project?.billingAddress == null
      ? (customerMainAddress?.display || null)
      : (project.billingAddress || null);

  // Persist a header name edit on its own. Commits only on a real change;
  // re-seeds the local query optimistically isn't needed — invalidate refetches.
  const saveName = useMutation({
    mutationFn: (name: string) =>
      updateProject(projectId as string, { name: name.trim() || null }),
    onSuccess: invalidate,
  });

  const startEditName = () => {
    setNameDraft(project?.name || '');
    setEditingName(true);
  };
  const commitName = () => {
    if (!editingName) return;
    setEditingName(false);
    const next = nameDraft.trim();
    if (next !== (project?.name || '')) saveName.mutate(next);
  };
  const cancelName = () => {
    setEditingName(false);
    setNameDraft(project?.name || '');
  };

  // Prohibited-items acknowledgment — one per project, saved immediately on
  // toggle. updateProject cascades it onto every draft phase so they can
  // reserve (the reserve gate reads each phase's copy). Optimistic: flip the
  // cached value on click so the checkbox responds instantly, roll back on
  // error, and reconcile with the server on settle.
  const saveAck = useMutation({
    mutationFn: (acknowledged: boolean) =>
      updateProject(projectId as string, {
        prohibited_items_acknowledged: acknowledged,
      }),
    onMutate: async (acknowledged: boolean) => {
      const key = queryKeys.projects.detail(projectId || '');
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Project>(key);
      qc.setQueryData<Project>(key, (old) =>
        old ? { ...old, prohibitedItemsAcknowledged: acknowledged } : old,
      );
      return { previous };
    },
    onError: (_err, _acknowledged, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKeys.projects.detail(projectId || ''), context.previous);
      }
    },
    onSettled: invalidate,
  });

  // Clear a phase's bin so the inline picker returns (one bin per phase).
  const removeBin = useMutation({
    mutationFn: (phaseId: string) => clearPhaseLines(phaseId),
    onSuccess: invalidate,
  });

  // Apply the Heavy material line to a phase (10cy/20cy bins only). `variables`
  // carries the phase id so the checkbox can show per-row pending. On success the
  // project is refetched — the server now reports hasHeavy, which keeps the box
  // checked across reloads, and the new line's price lands in the card footer.
  const applyHeavy = useMutation({
    mutationFn: (phaseId: string) => addPhaseHeavyLine(phaseId),
    onSuccess: invalidate,
    onError: (err: unknown) => {
      alert(err instanceof Error ? err.message : 'Could not add heavy material.');
    },
  });

  // Remove the Heavy material line from a phase (unchecking the box). `variables`
  // carries the phase id for per-row pending, mirroring applyHeavy. On success the
  // project is refetched — the server now reports hasHeavy=false, unchecking the box.
  const removeHeavy = useMutation({
    mutationFn: (phaseId: string) => removePhaseHeavyLine(phaseId),
    onSuccess: invalidate,
    onError: (err: unknown) => {
      alert(err instanceof Error ? err.message : 'Could not remove heavy material.');
    },
  });

  // Reserve a phase that has a bin but is still a draft (the "Not reserved"
  // retry, and the recovery path when the auto-reserve on bin-add failed).
  // `variables` carries the phase id so the card can show per-row pending.
  const reservePhaseMutation = useMutation({
    mutationFn: (phaseId: string) => reservePhase(phaseId),
    onSuccess: invalidate,
    onError: (err: unknown) => {
      alert(err instanceof Error ? err.message : 'Could not reserve this phase.');
    },
  });

  // Delete (archive) the last phase. Guarded server-side to a trailing draft.
  const deletePhaseMutation = useMutation({
    mutationFn: (phaseId: string) => deletePhase(phaseId),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
    },
  });

  // View a promoted phase's invoice. The doc id isn't on the phase payload, so
  // resolve it on click: pull the phase order's documents (same endpoint the
  // order sidebar uses), pick the invoice (finalized first, else the pro forma),
  // and open it in the document viewer. `variables` carries the phase id so the
  // card can show per-row loading/error.
  const openInvoice = useMutation({
    mutationFn: async (phaseId: string) => {
      const res = await apiFetch(
        `/booqable/orders/${encodeURIComponent(phaseId)}/documents`,
      );
      if (!res.ok) throw new Error(`Couldn’t load invoice (${res.status})`);
      const docs = (await res.json())?.data || [];
      const invoices = docs.filter((d: any) => d.document_type === 'invoice');
      const inv = invoices.find((d: any) => d.finalized) || invoices[0];
      if (!inv) throw new Error('No invoice for this phase yet.');
      return inv.id as string;
    },
    onSuccess: (docId) =>
      navigate(`/documents/${docId}`, { state: { fromProject: { id: projectId } } }),
  });

  // Persist a picked site location to the project. Also re-derive the
  // municipality (reverseGeocode → /rolloff-rates/lookup, the same chain the
  // order form uses) so phase tier pricing follows the new site. A failed
  // municipality lookup is non-fatal — the address still saves; the tier just
  // stays unresolved until a mappable location is set.
  const saveLocation = useMutation({
    mutationFn: async (loc: { lat: number; lng: number; address: string; name: string }) => {
      let municipality_id: string | undefined;
      try {
        const name = await getGeocodeConnector().reverseGeocodeMunicipality(loc.lat, loc.lng);
        if (name) {
          const res = await apiFetch(
            `/rolloff-rates/lookup?municipality=${encodeURIComponent(name)}`,
          );
          if (res.ok) municipality_id = (await res.json())?.municipality?.id || undefined;
        }
      } catch {
        // non-fatal — fall through and save the address without a municipality
      }
      await updateProject(projectId as string, {
        delivery_address: loc.address,
        delivery_latitude: loc.lat,
        delivery_longitude: loc.lng,
        delivery_location_name: loc.name || null,
        ...(municipality_id ? { municipality_id } : {}),
      });
    },
    onSuccess: invalidate,
  });

  // Clears the project's service location (the Location block's Remove action).
  const clearLocation = useMutation({
    mutationFn: () =>
      updateProject(projectId as string, {
        delivery_address: null,
        delivery_latitude: null,
        delivery_longitude: null,
        delivery_location_name: null,
      }),
    onSuccess: invalidate,
  });

  // Adds the picked location to the customer's saved locations (the picker's
  // "Add to customer's saved locations" checkbox).
  const saveLocationToCustomer = useMutation({
    mutationFn: (loc: { lat: number; lng: number; address: string; name: string }) =>
      addCustomerLocation(project!.customerId as string, {
        name: loc.name || null,
        address: loc.address || null,
        latitude: loc.lat,
        longitude: loc.lng,
      }),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: queryKeys.customers.locations(project?.customerId || ''),
      }),
  });

  // Keep the draft location in sync with the project's stored location. Only
  // re-seeds when the persisted location actually changes (after a save), so it
  // never clobbers an in-progress pick (which moves draftLocation, not the
  // project) mid-exploration.
  React.useEffect(() => {
    setDraftLocation(
      project?.deliveryLatitude != null && project?.deliveryLongitude != null
        ? {
            lat: project.deliveryLatitude,
            lng: project.deliveryLongitude,
            address: project.deliveryAddress || '',
            name: project.deliveryLocationName || '',
          }
        : null,
    );
  }, [
    project?.deliveryLatitude,
    project?.deliveryLongitude,
    project?.deliveryAddress,
    project?.deliveryLocationName,
  ]);

  // Commit the current draft pin to the project (the picker's "Add location").
  const handleAddLocationToProject = () => {
    if (draftLocation) saveLocation.mutate(draftLocation);
  };

  // Picker "Add to customer's saved locations" checkbox companion.
  const handleSaveLocationToCustomer = () => {
    if (draftLocation && project?.customerId) saveLocationToCustomer.mutate(draftLocation);
  };

  // Remove the project's location (picker Remove / cancel when there was none).
  const handleRemoveLocation = () => {
    setDraftLocation(null);
    clearLocation.mutate();
  };

  // "Choose from saved locations" — apply the picked saved location to the
  // project immediately (and reflect it in the draft).
  const handleLocationIndexSelect = (index: number) => {
    const loc = savedLocations[index];
    if (!loc) return;
    const picked = { lat: loc.lat, lng: loc.lng, address: loc.address, name: loc.name || '' };
    setDraftLocation(picked);
    saveLocation.mutate(picked);
  };

  const renderPhase = (phase: ProjectPhase, index: number) => {
    const promoted = !!phase.booqable_id;
    // A final pickup is pickup-only: it hauls the last bin away, so it carries no
    // bin of its own — the card hides the bin picker for it.
    const fp = isFinalPickup(phase);
    // Date pickers read from the local schedule (instant), not the query.
    const sched = schedule[index];
    const prevPromoted = index > 0 && !!schedule[index - 1]?.promoted;
    const nextPromoted = index < schedule.length - 1 && !!schedule[index + 1]?.promoted;
    // A paid phase is billed against a finalized invoice, so its window is frozen
    // — staff extend it via an Extra Day charge on the order, not by rescheduling.
    const paid = isPhasePaid(phase);
    const prevPaid = isPhasePaid(project?.phases[index - 1]);
    const nextPaid = isPhasePaid(project?.phases[index + 1]);
    // A promoted phase's OWN dates are editable here — staff reschedule a booked
    // phase from this card (the change is pushed to its Booqable order on save),
    // matching the timeline strip. An edge is locked when it touches a PAID phase
    // (frozen window), or is a hinge shared by two promoted phases (moving it
    // would reschedule both reservations at once).
    const startDisabled = paid || prevPaid || (promoted && prevPromoted);
    const endDisabled = paid || nextPaid || (promoted && nextPromoted);
    // Cascade is always ripple: a boundary edit translates the tail (durations
    // kept), so the only upper bound comes from a PROMOTED wall — ripple stops
    // there, so the edge can't cross that pinned promoted window and invert it.
    const prevSched = schedule[index - 1];
    const nextSched = schedule[index + 1];
    const deliveryMin = prevSched?.startDate ? addDays(prevSched.startDate, 1) : undefined;
    // Delivery moves this phase's start; if THIS phase is promoted its end is
    // pinned (ripple breaks at it), so the start can't reach the end.
    const deliveryMax = promoted && sched?.endDate ? addDays(sched.endDate, -1) : undefined;
    const pickupMin = sched?.startDate ? addDays(sched.startDate, 1) : undefined;
    // Pickup moves this phase's end against the NEXT phase's start; if the next
    // phase is promoted its end is pinned, so this end can't reach it.
    const pickupMax =
      nextPromoted && nextSched?.endDate ? addDays(nextSched.endDate, -1) : undefined;
    // Lines are only addable while the order is still editable — the same
    // window the order form allows: an un-promoted draft, or a promoted order
    // that hasn't gone out yet (new/draft/reserved). Once started/stopped the
    // inline picker is hidden; use "Open order" for those.
    const lineEditable =
      !promoted || ['new', 'draft', 'reserved'].includes(phase.booqableStatus || '');
    // Only the trailing phase (highest number), and only while it's still an
    // un-promoted draft, can be deleted — mirrors the backend guard. Keeps
    // numbering contiguous and protects live/invoiced phases.
    const lastPhaseNumber = Math.max(
      0,
      ...(project?.phases || []).map((p) => p.phaseNumber || 0),
    );
    const deletable = !promoted && (phase.phaseNumber || 0) === lastPhaseNumber;
    return (
      <Card key={phase.id} className={styles.phaseCard}>
        <div className={styles.cardHeader}>
          {/* Line 1: number chip · phase name · status pill (pinned right).
              This is the only clickable region — opens the order drawer. */}
          <div
            className={styles.headerTop}
            role="button"
            tabIndex={0}
            aria-label={`Open order for ${phase.phaseLabel || `Phase ${phase.phaseNumber}`}`}
            onClick={() => setDrawerPhase(phase)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setDrawerPhase(phase);
              }
            }}
          >
            <Text weight="semibold" className={styles.headerTitle}>
              {phase.phaseLabel || `Phase ${phase.phaseNumber}`}
            </Text>
            <OrderStatusPill status={phase.booqableStatus} onBrand />
          </div>
          {/* Line 2: order # link · action bar (invoice · ⋯) on a darker band.
              Not clickable, so the buttons act on their own. */}
          <div className={styles.headerBottom}>
            {/* Once promoted, the phase has an order: show its # as a link to the
                full order (new tab). Un-promoted drafts have no order yet — show
                nothing rather than the phase id, which only confused (it flips to
                the order # the moment an order exists). The link sizes to its
                text so only "#<number>" is clickable; the spacer below keeps the
                action bar right-aligned in both cases. */}
            {phase.orderNumber && (
              <Link
                className={styles.headerOrderLink}
                href={`/orders/${phase.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                #{phase.orderNumber}
              </Link>
            )}
            <div style={{ flexGrow: 1 }} />
            <div className={styles.headerActions}>
              {promoted && (
                <Tooltip content="View invoice" relationship="label">
                  <Button
                    appearance="transparent"
                    size="small"
                    icon={<Receipt20Regular />}
                    aria-label="View invoice"
                    className={styles.headerKebab}
                    disabled={openInvoice.isPending && openInvoice.variables === phase.id}
                    onClick={() => openInvoice.mutate(phase.id)}
                  />
                </Tooltip>
              )}
              {deletable && (
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                      appearance="transparent"
                      size="small"
                      icon={<MoreHorizontal20Regular />}
                      aria-label="Phase actions"
                      className={styles.headerKebab}
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem
                        className={styles.deleteMenuItem}
                        icon={
                          <Delete16Regular
                            style={{ color: tokens.colorPaletteRedForeground1 }}
                          />
                        }
                        onClick={() => setDeleteTarget(phase)}
                      >
                        Delete phase
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              )}
            </div>
          </div>
        </div>

        <div className={styles.cardBody}>
          {stage === 'planning' ? (
            fp ? (
              // A final pickup is pickup-only — no bin is delivered, so it shows a
              // single planned-pickup date, not a delivery→pickup window. The date
              // is the swap hinge (this phase's start === the prior phase's
              // pickup), so editing it moves the same boundary that collects the
              // last bin.
              <div className={styles.phaseDates}>
                <div className={styles.dateColumn}>
                  <Text className={styles.infoLabel}>Pickup</Text>
                  <DatePicker
                    size="medium"
                    hideIcon
                    value={isoToDate(sched?.startDate)}
                    onSelectDate={(date) => onPhaseDate(phase.id, 'start', date)}
                    formatDate={formatDateShort}
                    placeholder="Set pickup date"
                    minDate={isoToDate(deliveryMin) ?? undefined}
                    maxDate={isoToDate(deliveryMax) ?? undefined}
                    disabled={startDisabled}
                  />
                </div>
              </div>
            ) : (
            <div className={styles.phaseDates}>
              <div className={styles.dateColumn}>
                <Text className={styles.infoLabel}>Delivery</Text>
                <DatePicker
                  size="medium"
                  hideIcon
                  value={isoToDate(sched?.startDate)}
                  onSelectDate={(date) => onPhaseDate(phase.id, 'start', date)}
                  formatDate={formatDateShort}
                  placeholder="Set delivery date"
                  minDate={isoToDate(deliveryMin) ?? undefined}
                  maxDate={isoToDate(deliveryMax) ?? undefined}
                  disabled={startDisabled}
                />
              </div>
              <div className={styles.arrowWrap}>
                <ArrowRight20Regular />
              </div>
              <div className={styles.dateColumn}>
                <Text className={styles.infoLabel}>Pickup</Text>
                <DatePicker
                  size="medium"
                  hideIcon
                  value={isoToDate(sched?.endDate)}
                  onSelectDate={(date) => onPhaseDate(phase.id, 'end', date)}
                  formatDate={formatDateShort}
                  placeholder="Set pickup date"
                  minDate={isoToDate(pickupMin) ?? undefined}
                  maxDate={isoToDate(pickupMax) ?? undefined}
                  disabled={endDisabled}
                />
              </div>
            </div>
            )
          ) : fp ? (
            // Provisioning, final pickup: single read-only pickup date.
            <div className={styles.phaseDatesReadonly}>
              <div className={styles.dateColumn}>
                <Text className={styles.infoLabel}>Pickup</Text>
                <Text className={styles.dateValue}>
                  {sched?.startDate
                    ? formatDateShort(isoToDate(sched.startDate) as Date)
                    : '—'}
                </Text>
              </div>
            </div>
          ) : (
            // Provisioning: dates are locked to read-only — return via "Edit
            // schedule" to change them.
            <div className={styles.phaseDatesReadonly}>
              <div className={styles.dateColumn}>
                <Text className={styles.infoLabel}>Delivery</Text>
                <Text className={styles.dateValue}>
                  {sched?.startDate
                    ? formatDateShort(isoToDate(sched.startDate) as Date)
                    : '—'}
                </Text>
              </div>
              <div className={styles.arrowWrapReadonly}>
                <ArrowRight20Regular />
              </div>
              <div className={styles.dateColumn}>
                <Text className={styles.infoLabel}>Pickup</Text>
                <Text className={styles.dateValue}>
                  {sched?.endDate ? formatDateShort(isoToDate(sched.endDate) as Date) : '—'}
                </Text>
              </div>
            </div>
          )}

          {/* Paid phase: window is frozen against a finalized invoice. Steer
              staff to the order's Extra Day charge rather than a reschedule. */}
          {paid && (
            <div className={styles.paidDatesNote}>
              <LockClosed16Regular />
              <Text size={200}>
                Paid — extend this phase from its order&rsquo;s{' '}
                <Link onClick={() => navigate(`/orders/${phase.id}`)}>Extra Day charge</Link>.
              </Text>
            </div>
          )}

          {/* Planning: no bin UI — a binless phase is the scratchpad. Show an
              already-promoted phase's bin read-only for context. */}
          {stage === 'planning' && phase.itemCount > 0 && (
            <PhaseBinRow
              binLabel={phase.binLabel}
              binQuantity={phase.binQuantity}
              itemCount={phase.itemCount}
              readOnly
            />
          )}
          {stage === 'planning' && phase.itemCount === 0 && (
            <Text className={styles.binPlaceholder}>
              {fp ? 'Pickup only — no bin' : 'Lock schedule to add bins'}
            </Text>
          )}

          {/* A final pickup carries no bin — it hauls the last one away — so it
              never shows the bin picker (in either stage). */}
          {stage === 'provisioning' && fp && phase.itemCount === 0 && (
            <Text className={styles.binPlaceholder}>Pickup only — no bin</Text>
          )}

          {stage === 'provisioning' && !fp && lineEditable && phase.itemCount === 0 && (
              <PhaseLineAdd
                phaseId={phase.id}
                promoted={promoted}
                startDate={phase.startDate}
                endDate={phase.endDate}
                onAdded={(binLabel, priceInCents) => {
                  // Optimistically show the bin (label + price) the instant the
                  // line lands. Deliberately NO invalidate here: the mirror
                  // totals aren't reconciled yet, so refetching now reads stale
                  // 0-items/$0 and bounces the bin back to the picker spinner.
                  // onSettled (after reconcile) owns the single refetch that
                  // pulls the authoritative line title + final totals.
                  qc.setQueryData<Project>(
                    queryKeys.projects.detail(projectId || ''),
                    (old) =>
                      old
                        ? {
                            ...old,
                            phases: old.phases.map((ph) =>
                              ph.id === phase.id
                                ? {
                                    ...ph,
                                    binLabel: binLabel ?? ph.binLabel,
                                    binQuantity: ph.binQuantity || 1,
                                    itemCount: ph.itemCount || 1,
                                    // Adding a bin now auto-reserves the phase
                                    // (PhaseLineAdd's background chain). Show the
                                    // end-state status optimistically so the card
                                    // doesn't flash a "Not reserved" note during
                                    // the in-flight reserve; the onSettled refetch
                                    // reverts to draft only if reserve failed.
                                    booqableStatus: 'reserved',
                                    // Tier price (cents → dollars) so the footer
                                    // shows it before the reconcile refetch.
                                    price:
                                      priceInCents != null
                                        ? priceInCents / 100
                                        : ph.price,
                                  }
                                : ph,
                            ),
                          }
                        : old,
                  );
                }}
                onSettled={invalidate}
                defaultBin={defaultBin}
                autoAdd={autoAddBinPhaseIds.has(phase.id)}
                onAutoAddConsumed={() =>
                  setAutoAddBinPhaseIds((prev) => {
                    if (!prev.has(phase.id)) return prev;
                    const next = new Set(prev);
                    next.delete(phase.id);
                    return next;
                  })
                }
              />
          )}
          {stage === 'provisioning' && lineEditable && phase.itemCount > 0 && (
            <>
              <PhaseBinRow
                binLabel={phase.binLabel}
                binQuantity={phase.binQuantity}
                itemCount={phase.itemCount}
                removing={removeBin.isPending && removeBin.variables === phase.id}
                onRemove={() => removeBin.mutate(phase.id)}
                phaseId={phase.id}
                binLineId={phase.binLineId}
                binUnitPriceInCents={phase.binUnitPriceInCents}
                onPriceOverridden={invalidate}
              />
              {/* Heavy material add-on — shown only for 10cy/20cy bins. Checking it
                  applies a Heavy line to the phase order; unchecking removes it.
                  Disabled only while a mutation for this phase is in flight, so the
                  reflected state can't be double-toggled mid-request. */}
              {binIsHeavyEligible(phase.binLabel) && (
                <Checkbox
                  className={styles.heavyCheckbox}
                  label="Heavy material"
                  checked={
                    removeHeavy.isPending && removeHeavy.variables === phase.id
                      ? false
                      : !!phase.hasHeavy ||
                        (applyHeavy.isPending && applyHeavy.variables === phase.id)
                  }
                  disabled={
                    (applyHeavy.isPending && applyHeavy.variables === phase.id) ||
                    (removeHeavy.isPending && removeHeavy.variables === phase.id)
                  }
                  onChange={(_e, data) => {
                    if (data.checked === true) applyHeavy.mutate(phase.id);
                    else if (data.checked === false) removeHeavy.mutate(phase.id);
                  }}
                />
              )}
              {/* A bin normally auto-reserves the moment it lands. If that reserve
                  failed (e.g. no inventory for the window), the phase keeps the bin
                  but stays a draft — committing no inventory. Surface that here with
                  a one-click retry so it can't quietly sit in limbo. */}
              {(phase.booqableStatus === 'draft' || phase.booqableStatus === 'new') && (
                <div className={styles.notReservedRow}>
                  <Warning16Regular className={styles.notReservedIcon} />
                  <Text className={styles.notReservedText}>Not reserved</Text>
                  <Button
                    size="small"
                    appearance="subtle"
                    disabled={
                      reservePhaseMutation.isPending &&
                      reservePhaseMutation.variables === phase.id
                    }
                    onClick={() => reservePhaseMutation.mutate(phase.id)}
                  >
                    {reservePhaseMutation.isPending &&
                    reservePhaseMutation.variables === phase.id
                      ? 'Reserving…'
                      : 'Reserve'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <CardFooter className={styles.cardFooter}>
          <div className={styles.metaRow}>
            <Text size={300}>
              <strong>{money(phase.price)}</strong>
            </Text>
            <Text className={styles.infoLabel}>
              {phase.itemCount} {phase.itemCount === 1 ? 'item' : 'items'}
            </Text>
            {promoted && phase.paymentStatus && (
              <PaymentStatusPill status={phase.paymentStatus} inline />
            )}
            {openInvoice.isError && openInvoice.variables === phase.id && (
              <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
                {(openInvoice.error as Error)?.message || 'Couldn’t open invoice.'}
              </Text>
            )}
          </div>
        </CardFooter>
      </Card>
    );
  };

  // --- Header title slot --------------------------------------------------
  // The project name is edited in situ here: it reads as the page title but is
  // a button that swaps to an Input on click. The immutable P-#### identifier
  // and status badge trail it.
  const projectName = project?.name || '';
  const title = (
    <div className={styles.title}>
      {/* Breadcrumb: Projects > {name}. Mirrors the orders/customers
          profile breadcrumb so /projects and /projects/:id share visual
          identity. */}
      <Link
        onClick={(e) => { e.preventDefault(); navigate('/projects'); }}
        href="/projects"
        style={{
          fontSize: tokens.fontSizeBase500,
          fontWeight: tokens.fontWeightSemibold,
          lineHeight: tokens.lineHeightBase300,
          color: tokens.colorNeutralForeground1,
        }}
      >
        Projects
      </Link>
      <ChevronRight16Regular style={{ color: tokens.colorNeutralForeground3 }} />
      <Box24Regular />
      {project ? (
        <div className={styles.nameWrap}>
          {editingName ? (
            <Input
              className={styles.nameInput}
              value={nameDraft}
              autoFocus
              aria-label="Project name"
              placeholder="Project name"
              disabled={saveName.isPending}
              onChange={(_, d) => setNameDraft(d.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur(); // blur → commitName
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelName();
                }
              }}
              onBlur={commitName}
            />
          ) : (
            <Tooltip content="Rename project" relationship="label">
              <button type="button" className={styles.nameButton} onClick={startEditName}>
                <Text
                  weight="semibold"
                  size={400}
                  className={projectName ? undefined : styles.nameEmpty}
                >
                  {projectName || 'Untitled project'}
                </Text>
                <Edit16Regular className={styles.nameEditIcon} />
              </button>
            </Tooltip>
          )}
          <Text weight="semibold" size={400} className={styles.idSuffix}>
            (P-{project.projectNumber})
          </Text>
          <ProjectStatusBadge status={project.status} />
        </div>
      ) : (
        <Text weight="semibold" size={400}>
          Project
        </Text>
      )}
    </div>
  );

  const phaseCount = project?.phases.length ?? 0;
  const projectTotal = (project?.phases || []).reduce((sum, p) => sum + (p.price || 0), 0);
  const paidTotal = (project?.phases || []).reduce((sum, p) => sum + (p.paidAmount || 0), 0);

  // The add-swap tile — the whole dashed slot is the click target. Doubles as
  // the empty-state CTA (no phases yet) so the user doesn't have to reach for the
  // top-right button. Adding no longer requires saving first: unsaved date edits
  // are preserved across the add (the reseed effect merges instead of discarding).
  // The card inserts optimistically, so there's no "Adding…" wait to surface.
  // Phase creation is gated on a saved service location AND the prohibited-items
  // acknowledgment — a phase minted without either lands its order in a fail
  // state. Applies to every phase (the location can be cleared mid-project), and
  // the caption surfaces whichever requirement is still missing.
  const missingLocation =
    !!project && (project.deliveryLatitude == null || project.deliveryLongitude == null);
  const missingAck = !!project && !project.prohibitedItemsAcknowledged;
  const phaseCreationBlocked = missingLocation || missingAck;
  const addSwapCaption =
    missingLocation && missingAck
      ? 'Add a service location and acknowledge prohibited items to add a phase'
      : missingLocation
        ? 'Add a service location to add a phase'
        : missingAck
          ? 'Acknowledge prohibited items to add a phase'
          : 'Each phase is its own invoiceable order';
  // The final pickup closes out the project: once it exists no more phases can
  // be added, so the add tile drops away entirely (mirrors the backend guard).
  const hasFinalPickup = (project?.phases || []).some(isFinalPickup);
  // The dashed tile that trails the phase grid. With no phases it's the
  // empty-state CTA that adds the initial drop directly. Once a phase exists the
  // slot holds two explicit actions — a phase can be either another swap or the
  // project-closing final pickup — shown as a small segmented pair (no dropdown).
  const addTileButton = (
    <button
      type="button"
      className={mergeClasses(styles.addSlot, phaseCount === 0 && styles.addSlotEmpty)}
      disabled={addPhase.isPending || phaseCreationBlocked}
      onClick={() => handleAddPhase(false)}
    >
      <span className={styles.addSlotInner}>
        <Add20Regular />
        Add first phase
      </span>
      <span className={styles.addSlotCaption}>{addSwapCaption}</span>
    </button>
  );
  const addActionTile = (
    <div className={styles.addSlotStatic}>
      <div className={styles.addSlotButtons}>
        <Button
          appearance="secondary"
          className={styles.addActionButton}
          icon={<ArrowSwap24Regular />}
          disabled={addPhase.isPending || phaseCreationBlocked}
          onClick={() => handleAddPhase(false)}
        >
          Add swap
        </Button>
        <Button
          appearance="secondary"
          className={styles.addActionButton}
          icon={<VehicleTruck24Regular />}
          disabled={addPhase.isPending || phaseCreationBlocked}
          onClick={() => handleAddPhase(true)}
        >
          Add final pickup
        </Button>
      </div>
      <span className={styles.addSlotCaption}>{addSwapCaption}</span>
    </div>
  );
  const addSwapSlot = hasFinalPickup ? null : phaseCount === 0 ? addTileButton : addActionTile;

  return (
    <div className={styles.workspaceCard}>
      <Header
        inline
        stackTitleOnMobile
        customTitle={title}
        customActions={
          <div className={styles.headerActionGroup}>
            <Tooltip content="Project settings" relationship="label">
              <Button
                appearance="subtle"
                icon={<Settings20Regular />}
                aria-label="Project settings"
                disabled={!project}
                onClick={openSettings}
              />
            </Tooltip>
          </div>
        }
      />

      <div className={styles.body}>
        {isLoading && (
          <div className={styles.centered}>
            <Spinner label="Loading project…" />
          </div>
        )}

        {isError && (
          <div className={styles.centered}>
            <Text>Couldn’t load this project.</Text>
          </div>
        )}

        {project && (
          <div className={styles.workArea}>
            <div className={styles.topRow}>
              <div className={styles.topColumn}>
                <CustomerSection
                  selectedCustomer={customerForCard}
                  onCustomerSelect={() => {}}
                  onCustomerClear={() => {}}
                  showIcon={false}
                  readOnly
                />
                <LocationSection
                  customerLocations={locationSectionLocations}
                  selectedLocationIndex={null}
                  onLocationIndexSelect={handleLocationIndexSelect}
                  currentLocation={
                    draftLocation ? { lat: draftLocation.lat, lng: draftLocation.lng } : null
                  }
                  currentLocationAddress={draftLocation?.address || ''}
                  currentLocationName={draftLocation?.name || ''}
                  onLocationPickerSelect={(loc) => setDraftLocation(loc)}
                  hasCustomerSelected={!!project.customerId}
                  onSaveLocationToCustomer={handleSaveLocationToCustomer}
                  onAddLocationToOrder={handleAddLocationToProject}
                  onRemoveLocation={handleRemoveLocation}
                  showIcon={false}
                />
                <div className={styles.ackBar}>
                  <div className={styles.ackHeaderRow}>
                    <Text weight="semibold" size={300}>
                      Prohibited items
                      {!project.prohibitedItemsAcknowledged && (
                        <span className={styles.requiredAsterisk}> *</span>
                      )}
                    </Text>
                    {!project.prohibitedItemsAcknowledged && (
                      <Tooltip
                        content="Phases can’t be reserved until the customer acknowledges the prohibited-items list."
                        relationship="label"
                      >
                        <Info16Regular tabIndex={0} className={styles.infoIcon} />
                      </Tooltip>
                    )}
                  </div>
                  <ProhibitedItemsAck
                    acknowledged={project.prohibitedItemsAcknowledged}
                    onChange={(checked) => saveAck.mutate(checked)}
                  />
                </div>
              </div>
              <div className={styles.topColumn}>
                <BillingAddressSection
                  savedBillingAddress={effectiveBillingAddress}
                  showIcon={false}
                  emptyLabel="Add billing address"
                  onAddBillingAddress={openBillingDialog}
                  onRemoveBillingAddress={() => removeBilling.mutate()}
                />
                <DetailsSection
                  editableFields={[
                    {
                      key: 'poNumber',
                      label: 'PO number',
                      value: detailsForm.poNumber,
                      onChange: (value) => editDetail('poNumber', value),
                    },
                    {
                      key: 'contractNumber',
                      label: 'Contract number',
                      value: detailsForm.contractNumber,
                      onChange: (value) => editDetail('contractNumber', value),
                    },
                    {
                      key: 'solicitationNumber',
                      label: 'Solicitation number',
                      value: detailsForm.solicitationNumber,
                      onChange: (value) => editDetail('solicitationNumber', value),
                    },
                  ]}
                  dirty={detailsDirty}
                  saving={saveDetails.isPending}
                  saveError={
                    saveDetails.isError
                      ? (saveDetails.error as Error)?.message || 'Save failed.'
                      : null
                  }
                  onSave={() => saveDetails.mutate()}
                  onDiscard={discardDetails}
                  showIcon={false}
                />
              </div>
            </div>

          <div className={styles.totalsBar}>
            <Text size={300}>
              <strong>{phaseCount}</strong> {phaseCount === 1 ? 'phase' : 'phases'}
            </Text>
            <Text size={300}>
              Project total <strong>{money(projectTotal)}</strong>
            </Text>
            <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
              Collected {money(paidTotal)}
            </Text>
          </div>

          <div className={styles.sectionHeader}>
            <Text weight="semibold" size={400}>
              Phases
            </Text>
          </div>

          {/* Combined schedule panel: timeline overview + stage-aware controls in
              one mounted unit, so they reserve space together (no grid reflow). */}
          <PhaseSchedulePanel
            segments={timelineSegments}
            stage={stage}
            saveStatus={saveStatus}
            saveError={(saveSchedule.error as Error)?.message}
            hasChanges={hasScheduleChanges}
            canLock={allPhasesDated}
            hasPhases={phaseCount > 0}
            defaultBin={defaultBin}
            onLock={() => setLockDialogOpen(true)}
            onEditSchedule={() => setStageOverride('planning')}
            onDiscard={discardSchedule}
            onRetrySave={() => saveSchedule.mutate()}
            // Drag-to-edit only on fine pointers; on mobile the strip is a
            // read-only overview and editing happens on the cards below.
            onBoundaryEdit={isMobile ? undefined : onBoundaryEdit}
          />

          <div className={styles.timeline}>
            {project.phases.map(renderPhase)}
            {addSwapSlot}
          </div>
          </div>
        )}
      </div>

      <PhaseOrderDrawer
        phase={drawerPhase}
        open={!!drawerPhase}
        onClose={() => setDrawerPhase(null)}
        onOpenFullOrder={(orderId) => navigate(`/orders/${orderId}`)}
      />

      {/* Project settings — right-side drawer. Reserved for future per-project
          settings (the cascade-mode toggle lived here but is now always ripple). */}
      <Drawer
        type="overlay"
        separator
        position="end"
        size="medium"
        open={settingsOpen}
        onOpenChange={(_, { open }) => setSettingsOpen(open)}
        modalType="modal"
      >
        <DrawerHeader className={styles.settingsHeader}>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close settings"
                icon={<Dismiss24Regular />}
                onClick={() => setSettingsOpen(false)}
              />
            }
          >
            Project settings
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <div className={styles.settingsSection}>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              No project settings yet.
            </Text>
          </div>
        </DrawerBody>
      </Drawer>

      {/* Billing-address dialog — project-owned override. Mirrors the order
          form's billing dialog: a Saved-address picker (the customer's address
          vs. a new one) over the shared AddressFields. On save we persist the
          composed one-line display into project.billingAddress. */}
      <Dialog
        open={billingDialogOpen}
        onOpenChange={(_, data) => setBillingDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Billing address</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {customerMainAddress && (
                  <div>
                    <Label htmlFor="projectSavedAddress" weight="semibold">Saved address</Label>
                    <Dropdown
                      id="projectSavedAddress"
                      size="large"
                      value={billingMode === 'customer' ? customerMainAddress.display : 'New address'}
                      selectedOptions={[billingMode]}
                      onOptionSelect={(_, data) => {
                        const next = (data.optionValue as 'customer' | 'new') || 'new';
                        setBillingMode(next);
                        if (next === 'customer') {
                          const f = customerMainAddress.fields;
                          setBillingLine1(f.address1);
                          setBillingLine2(f.address2);
                          setBillingCity(f.city);
                          setBillingRegion(f.region);
                          setBillingPostal(f.zipcode);
                          setBillingCountry(f.country);
                        }
                      }}
                      style={{ width: '100%' }}
                    >
                      <Option value="new">New address</Option>
                      <Option value="customer">{customerMainAddress.display}</Option>
                    </Dropdown>
                  </div>
                )}

                <AddressFields
                  addressLine1={billingLine1}
                  onAddressLine1Change={(v) => { setBillingLine1(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  addressLine2={billingLine2}
                  onAddressLine2Change={(v) => { setBillingLine2(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  city={billingCity}
                  onCityChange={(v) => { setBillingCity(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  region={billingRegion}
                  onRegionChange={(v) => { setBillingRegion(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  postalCode={billingPostal}
                  onPostalCodeChange={(v) => { setBillingPostal(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  country={billingCountry}
                  onCountryChange={(v) => { setBillingCountry(v); if (billingMode === 'customer') setBillingMode('new'); }}
                  idPrefix="projectBilling"
                  required
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setBillingDialogOpen(false)}
                disabled={saveBilling.isPending}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => saveBilling.mutate(composeBillingDisplay())}
                disabled={saveBilling.isPending || !billingComplete}
              >
                Save
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete-phase confirm. Only reachable from the last draft phase's
          ⋯ menu; the backend re-checks the trailing-draft guard. */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(_, data) => {
          if (!data.open) setDeleteTarget(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete phase?</DialogTitle>
            <DialogContent>
              {deleteTarget && (
                <Text>
                  This removes{' '}
                  <strong>
                    {deleteTarget.phaseDisplayId ||
                      `Phase ${deleteTarget.phaseNumber}`}
                  </strong>
                  {deleteTarget.phaseLabel ? ` (${deleteTarget.phaseLabel})` : ''} from
                  the project. This can’t be undone.
                </Text>
              )}
              {deletePhaseMutation.isError && (
                <Text style={{ color: tokens.colorPaletteRedForeground1, display: 'block', marginTop: '8px' }}>
                  {(deletePhaseMutation.error as Error)?.message || 'Delete failed.'}
                </Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deletePhaseMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                className={styles.deleteConfirmButton}
                disabled={deletePhaseMutation.isPending}
                onClick={() => deleteTarget && deletePhaseMutation.mutate(deleteTarget.id)}
              >
                {deletePhaseMutation.isPending ? 'Deleting…' : 'Delete phase'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Lock schedule → choose the bin every phase defaults to. Picking one
          pre-points each empty phase's "Add bin" at it (a same-size swap job is
          then one click per phase); skip to add bins individually. */}
      <Dialog
        open={lockDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open) setLockDialogOpen(false);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>What bin will these phases use?</DialogTitle>
            <DialogContent>
              <div className={styles.lockDialogBody}>
                <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
                  Pick the bin most phases use. Each phase pre-fills with it, and you
                  can change or skip any.
                </Text>
                <ProductSearchInput
                  native
                  nativeFullLabel
                  nativeValue={defaultBin?.stockItem.id ?? ''}
                  size="medium"
                  allowedProductTypes={['rental']}
                  onInventoryItemSelect={(group, stockItem) => {
                    setDefaultBin({
                      group,
                      stockItem,
                      label: fullBinLabel(group.name, stockItem.variation_name),
                    });
                  }}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                className={styles.lockDialogButton}
                onClick={() => confirmLock(false)}
              >
                Skip, add bins individually
              </Button>
              <Button
                appearance="primary"
                className={styles.lockDialogButton}
                disabled={!defaultBin}
                onClick={() => confirmLock(true)}
              >
                Lock &amp; add bins
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ProjectDetails;
